import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import UrduKeyboard from '../../Components/UrduKeyboard';

function SalesTab({ sales, companyLanguage, handleEditSale, handleDeleteSale }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchUrduKeyboard, setShowSearchUrduKeyboard] = useState(false);
  const searchInputRef = useRef(null);
  // Set today's date as default for date filters
  const today = new Date().toISOString().split('T')[0];
  const [filterDateFrom, setFilterDateFrom] = useState(today);
  const [filterDateTo, setFilterDateTo] = useState(today);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterSeller, setFilterSeller] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(true); // Auto-open filters
  const [selectedSales, setSelectedSales] = useState([]);
  const [showGeneralSales, setShowGeneralSales] = useState(false);
  const [generalSales, setGeneralSales] = useState([]);
  const [isLoadingGeneralSales, setIsLoadingGeneralSales] = useState(false);

  const fetchGeneralSales = async () => {
    setIsLoadingGeneralSales(true);
    try {
      const token = localStorage.getItem('companyToken');
      if (!token) return;

      const response = await api.get('/ledger/general-sales', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('General sales API response:', response.data);
      console.log('General sales array:', response.data?.generalSales);
      
      // Transform general sales to match regular sales format
      const transformedGeneralSales = (response.data?.generalSales || []).map(gs => {
        console.log('Processing general sale:', gs);
        return {
          _id: gs._id,
          serialNumber: gs._id?.slice(-6) || 'N/A',
          date: gs.date || new Date().toISOString().split('T')[0],
          supplierName: gs.partyId?.name || '',
          supplierNameUrdu: gs.partyId?.nameUrdu || '',
          sellerName: '',
          sellerNameUrdu: '',
          totalAmount: gs.amount || 0,
          isGeneralSale: true // Flag to identify general sales
        };
      });
      
      console.log('Transformed general sales:', transformedGeneralSales);
      console.log('Transformed general sales count:', transformedGeneralSales.length);
      setGeneralSales(transformedGeneralSales);
    } catch (error) {
      console.error('Fetch general sales error:', error);
      toast.error(companyLanguage === 'ur' ? 'جنرل سیلز لوڈ کرنے میں ناکام' : 'Failed to load general sales');
    } finally {
      setIsLoadingGeneralSales(false);
    }
  };

  // Fetch General Sales when toggle is enabled
  useEffect(() => {
    if (showGeneralSales) {
      fetchGeneralSales();
    } else {
      // Clear general sales when toggle is off
      setGeneralSales([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGeneralSales]);

  // Combine regular sales and general sales
  const allSales = useMemo(() => {
    if (showGeneralSales && generalSales.length > 0) {
      const combined = [...sales, ...generalSales];
      console.log('Combined sales:', combined.length, 'Regular:', sales.length, 'General:', generalSales.length);
      return combined;
    }
    return sales;
  }, [sales, generalSales, showGeneralSales]);

  // Get unique suppliers and sellers for filter dropdowns
  const uniqueSuppliers = useMemo(() => {
    const suppliers = new Set();
    allSales.forEach(sale => {
      const name = sale.supplierNameUrdu || sale.supplierName;
      if (name) suppliers.add(name);
    });
    return Array.from(suppliers).sort();
  }, [allSales]);

  const uniqueSellers = useMemo(() => {
    const sellers = new Set();
    allSales.forEach(sale => {
      const name = sale.sellerNameUrdu || sale.sellerName;
      if (name) sellers.add(name);
    });
    return Array.from(sellers).sort();
  }, [allSales]);

  // Filter sales based on all filter criteria
  const filteredSales = useMemo(() => {
    return allSales.filter(sale => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const supplierName = (sale.supplierNameUrdu || sale.supplierName || '').toLowerCase();
        const sellerName = (sale.sellerNameUrdu || sale.sellerName || '').toLowerCase();
        const serialNumber = (sale.serialNumber || '').toString().toLowerCase();
        
        if (!supplierName.includes(searchLower) && 
            !sellerName.includes(searchLower) && 
            !serialNumber.includes(searchLower)) {
          return false;
        }
      }

      // Date range filter
      if (filterDateFrom && sale.date) {
        const saleDate = new Date(sale.date);
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        saleDate.setHours(0, 0, 0, 0);
        if (saleDate < fromDate) return false;
      }
      if (filterDateTo && sale.date) {
        const saleDate = new Date(sale.date);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        saleDate.setHours(0, 0, 0, 0);
        if (saleDate > toDate) return false;
      }

      // Supplier filter
      if (filterSupplier) {
        const supplierName = sale.supplierNameUrdu || sale.supplierName || '';
        if (supplierName !== filterSupplier) return false;
      }

      // Seller filter
      if (filterSeller) {
        const sellerName = sale.sellerNameUrdu || sale.sellerName || '';
        if (sellerName !== filterSeller) return false;
      }

      // Amount range filter
      const amount = sale.totalAmount || 0;
      if (filterAmountMin) {
        if (amount < parseFloat(filterAmountMin)) return false;
      }
      if (filterAmountMax) {
        if (amount > parseFloat(filterAmountMax)) return false;
      }

      return true;
    });
  }, [allSales, searchTerm, filterDateFrom, filterDateTo, filterSupplier, filterSeller, filterAmountMin, filterAmountMax]);

  // Handle Urdu keyboard input for search field
  const handleSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSearchTerm((prev) => prev ? prev.slice(0, -1) : '');
    } else {
      setSearchTerm((prev) => (prev || '') + key);
    }
  };

  // Toggle Urdu keyboard on search input click
  const handleSearchInputClick = () => {
    setShowSearchUrduKeyboard(prev => !prev);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSupplier('');
    setFilterSeller('');
    setFilterAmountMin('');
    setFilterAmountMax('');
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterDateFrom || filterDateTo || filterSupplier || filterSeller || filterAmountMin || filterAmountMax;

  // Select All functionality
  const handleSelectAll = () => {
    const allFilteredIds = filteredSales.map(sale => sale._id);
    const allSelected = allFilteredIds.every(id => selectedSales.includes(id));
    
    if (allSelected) {
      // Deselect all filtered sales
      setSelectedSales(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered sales
      setSelectedSales(prev => {
        const newSelection = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleToggleSale = (saleId) => {
    setSelectedSales(prev => {
      if (prev.includes(saleId)) {
        return prev.filter(id => id !== saleId);
      } else {
        return [...prev, saleId];
      }
    });
  };

  const isAllSelected = filteredSales.length > 0 && filteredSales.every(sale => selectedSales.includes(sale._id));

  // Bulk Delete functionality
  const handleBulkDelete = async () => {
    if (selectedSales.length === 0) return;
    
    const confirmMessage = companyLanguage === 'ur' 
      ? `کیا آپ واقعی ${selectedSales.length} سیلز ختم کرنا چاہتے ہیں؟`
      : `Are you sure you want to delete ${selectedSales.length} sales?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('companyToken');
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'لاگ ان کریں' : 'Please login');
        return;
      }

      let successCount = 0;
      let failCount = 0;
      
      // Delete all selected sales in parallel
      const deletePromises = selectedSales.map(async (saleId) => {
        try {
          // Check if it's a general sale
          const sale = allSales.find(s => s._id === saleId);
          if (sale?.isGeneralSale) {
            // Delete general sale (ledger transaction)
            await api.delete(`/ledger/transactions/${saleId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Delete regular sale
            await api.delete(`/sale/company/${saleId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
          return { success: true, saleId };
        } catch (error) {
          console.error(`Failed to delete sale ${saleId}:`, error);
          return { success: false, saleId };
        }
      });

      const results = await Promise.all(deletePromises);
      successCount = results.filter(r => r.success).length;
      failCount = results.filter(r => !r.success).length;

      setSelectedSales([]);
      
      if (successCount > 0) {
        toast.success(
          companyLanguage === 'ur' 
            ? `${successCount} سیلز کامیابی سے ختم ہو گئے`
            : `${successCount} sales deleted successfully`
        );
        // Refresh sales list
        if (window.location.reload) {
          setTimeout(() => window.location.reload(), 500);
        }
        
        // Refresh general sales if enabled
        if (showGeneralSales) {
          fetchGeneralSales();
        }
      }
      
      if (failCount > 0) {
        toast.error(
          companyLanguage === 'ur' 
            ? `${failCount} سیلز ختم کرنے میں ناکام`
            : `Failed to delete ${failCount} sales`
        );
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(companyLanguage === 'ur' ? 'ختم کرنے میں ناکام' : 'Failed to delete sales');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card animate-fade-in-up">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className={`text-2xl font-bold text-brand-header ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
          {companyLanguage === 'ur' ? 'سیلز' : 'Sales'} 
          <span className="text-lg text-gray-500 ml-2">
            ({filteredSales.length}/{allSales.length})
          </span>
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 ${
              showFilters || hasActiveFilters
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {companyLanguage === 'ur' ? 'فلٹر' : 'Filter'}
            {hasActiveFilters && (
              <span className="bg-white text-green-500 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                !
              </span>
            )}
          </button>
          <button
            onClick={() => navigate(companyLanguage === 'ur' ? '/sale/create' : '/sale/create?lang=en')}
            className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {companyLanguage === 'ur' ? 'نیا سیل' : 'Create New Sale'}
          </button>
          <button
            onClick={() => navigate('/general-sale')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {companyLanguage === 'ur' ? 'جنرل سیل' : 'General Sale'}
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="mb-2 p-1.5 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border border-green-200 animate-fade-in-down shadow-lg">
          <div className="flex justify-between items-center mb-1">
            <h3 className={`font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'فلٹرز' : 'Filters'}
            </h3>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded-lg transition-all"
                >
                  {companyLanguage === 'ur' ? 'ری سیٹ' : 'Reset'}
                </button>
              )}
              <button
                onClick={() => setShowFilters(false)}
                className="px-2 py-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-xs rounded-lg transition-all"
              >
                {companyLanguage === 'ur' ? 'بند کریں' : 'Close'}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1.5">
            {/* Search */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تلاش:' : 'Search:'}
              </label>
              <input
                type="text"
                ref={searchInputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleSearchInputClick}
                placeholder={companyLanguage === 'ur' ? 'سپلائر، بیچنے والا، نمبر...' : 'Supplier, Seller, Number...'}
                className={`w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                style={companyLanguage === 'ur' ? { 
                  fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                  lineHeight: '1.8',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  fontSize: '12px',
                  height: 'auto',
                  minHeight: '32px',
                  display: 'block',
                  overflow: 'visible',
                  verticalAlign: 'middle'
                } : {}}
                dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                lang={companyLanguage === 'ur' ? 'ur' : 'en'}
              />
            </div>

            {/* Date From */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ سے:' : 'Date From:'}
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Date To */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ تک:' : 'Date To:'}
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Supplier Filter */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'سپلائر:' : 'Supplier:'}
              </label>
              <select
                value={filterSupplier}
                onChange={(e) => setFilterSupplier(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              >
                <option value="">{companyLanguage === 'ur' ? 'تمام' : 'All'}</option>
                {uniqueSuppliers.map(supplier => (
                  <option key={supplier} value={supplier}>{supplier}</option>
                ))}
              </select>
            </div>

            {/* Seller Filter */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'بیچنے والا:' : 'Seller:'}
              </label>
              <select
                value={filterSeller}
                onChange={(e) => setFilterSeller(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              >
                <option value="">{companyLanguage === 'ur' ? 'تمام' : 'All'}</option>
                {uniqueSellers.map(seller => (
                  <option key={seller} value={seller}>{seller}</option>
                ))}
              </select>
            </div>

            {/* Amount Min */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'کم از کم رقم:' : 'Min Amount:'}
              </label>
              <input
                type="number"
                value={filterAmountMin}
                onChange={(e) => setFilterAmountMin(e.target.value)}
                placeholder="0"
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Amount Max */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'زیادہ سے زیادہ رقم:' : 'Max Amount:'}
              </label>
              <input
                type="number"
                value={filterAmountMax}
                onChange={(e) => setFilterAmountMax(e.target.value)}
                placeholder="∞"
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>
          </div>
        </div>
      )}

      {/* General Sales Toggle */}
      <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showGeneralSales}
              onChange={(e) => setShowGeneralSales(e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
            />
            <span className={`text-sm font-semibold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'جنرل سیلز بھی دکھائیں' : 'Show General Sales'}
            </span>
          </label>
          {isLoadingGeneralSales && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          )}
        </div>
        {showGeneralSales && generalSales.length > 0 && (
          <span className={`text-xs text-blue-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
            {generalSales.length} {companyLanguage === 'ur' ? 'جنرل سیلز' : 'General Sales'}
          </span>
        )}
      </div>

      {allSales.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'ابھی کوئی سیل نہیں' : 'No sales found yet'}</p>
          <p className="text-sm mt-2">
            {companyLanguage === 'ur' ? 'نیا سیل بنانے کے لیے اوپر بٹن دبائیں' : 'Use the button above to create a new sale'}
          </p>
        </div>
      ) : filteredSales.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'فلٹر کے مطابق کوئی سیل نہیں ملا' : 'No sales match the filters'}</p>
          <button
            onClick={resetFilters}
            className="mt-4 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all"
          >
            {companyLanguage === 'ur' ? 'فلٹرز ری سیٹ کریں' : 'Reset Filters'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header Row */}
          {filteredSales.length > 0 && (
            <div className="bg-gray-100 border border-brand-card rounded-lg py-2 px-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header cursor-pointer"
                  />
                  <span className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                    {companyLanguage === 'ur' ? 'سب منتخب' : 'Select All'}
                  </span>
                  {selectedSales.length > 0 && (
                    <span className={`text-sm text-gray-600 ml-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      ({selectedSales.length} {companyLanguage === 'ur' ? 'منتخب' : 'selected'})
                    </span>
                  )}
                </div>
                {selectedSales.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {companyLanguage === 'ur' ? `ختم کریں (${selectedSales.length})` : `Delete (${selectedSales.length})`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-7 gap-4 items-center" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'منتخب' : 'Select'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'سیل' : 'Sale'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'بیچنے والا' : 'Seller'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'کل رقم' : 'Total Amount'}
                </div>
                <div className={`text-sm font-bold text-gray-700 text-center`}>
                  {companyLanguage === 'ur' ? 'عمل' : 'Actions'}
                </div>
              </div>
            </div>
          )}
          
          {/* Sale Items */}
          {filteredSales.map((sale) => {
            const isSelected = selectedSales.includes(sale._id);
            return (
            <div
              key={sale._id}
              className={`border border-brand-card rounded-lg py-2 px-3 hover:shadow-md transition-all ${
                isSelected ? 'bg-emerald-50 border-green-400' : 'bg-white'
              }`}
            >
              <div className="grid grid-cols-7 gap-4 items-center" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleSale(sale._id)}
                    className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header cursor-pointer"
                  />
                </div>
                <div className={`font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {sale.isGeneralSale && (
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded mr-1">
                      {companyLanguage === 'ur' ? 'جنرل' : 'General'}
                    </span>
                  )}
                  {companyLanguage === 'ur' ? 'سیل #' : 'Sale #'}{sale.serialNumber || sale._id.slice(-6)}
                </div>
                <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {new Date(sale.date).toLocaleDateString()}
                </div>
                <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {sale.supplierNameUrdu || sale.supplierName || 'N/A'}
                </div>
                <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {sale.sellerNameUrdu || sale.sellerName || 'N/A'}
                </div>
                <div className={`text-sm font-semibold text-green-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  Rs. {(sale.totalAmount || 0).toLocaleString()}
                </div>
                <div className="flex gap-2 justify-center">
                  {!sale.isGeneralSale ? (
                    <>
                      <button
                        onClick={() => navigate(`/sale/view/${sale._id}?lang=${companyLanguage === 'ur' ? 'ur' : 'en'}`)}
                        className={`px-2 py-1 bg-brand-header hover:bg-emerald-800 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      >
                        {companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                      </button>
                      <button
                        onClick={() => handleEditSale && handleEditSale(sale)}
                        className={`px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      >
                        {companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit'}
                      </button>
                      <button
                        onClick={() => handleDeleteSale && handleDeleteSale(sale._id)}
                        className={`px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      >
                        {companyLanguage === 'ur' ? 'ختم کریں' : 'Delete'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => navigate('/general-sale')}
                      className={`px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    >
                      {companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}
      <UrduKeyboard
        isVisible={showSearchUrduKeyboard}
        onKeyPress={handleSearchUrduKeyPress}
        onClose={() => setShowSearchUrduKeyboard(false)}
      />
    </div>
  );
}

export default SalesTab;


